// AI JOBS wire contract — the ONE definition shared byte-for-byte by the client
// (src/backend/aiJobsClient.ts) and the Supabase edge function (supabase/functions/ai-jobs).
// Pure TypeScript (browser / Deno / Node): only the sibling protocol file is imported.
//
// What a JOB is (Owner contract 2026-09-13 §6 / §17–§26): the durable, server-owned record of one
// ADVANCED AI review. The client mints the job id (it doubles as the idempotency key), uploads the
// photos and the context once, and gets the job back at once; the server keeps running the analysis
// after the answer was sent, persists the result before anything else happens, and only then tells
// the device. The UI's lifecycle never decides the job's lifecycle.
//
// Final Owner Decision 2026-09-14 (ratification CA-9 / CA-10 / D-CA-6 / D-CA-7): a job is about ONE
// checklist row — its linked photos, its guidance, the row's AI observations and the inspector's note.
// The result is a cautious second opinion (what can be confirmed, what it might be, what cannot be
// confirmed, what to check on site, what to photograph); it never carries a report draft to adopt.
// A follow-up question is a new job that points at the one before it and carries the row's whole
// conversation so far.
import { AI_V2_ERROR_STATUS, AI_V2_LIMITS, AI_V2_PROTOCOL_VERSION, checkAiV2Quota, isAiV2Locale, isAiV2MediaType, type AiV2AreaContext, type AiV2ChecklistItem, type AiV2ErrorCode, type AiV2Locale, type AiV2Photo, type AiV2Quota } from './aiV2Protocol.ts';

export const AI_JOBS_PROTOCOL_VERSION = 1;
export const AI_JOBS_FUNCTION_PATH = '/functions/v1/ai-jobs';

export const AI_JOBS_LIMITS = Object.freeze({
  // Photos per advanced review: the row's linked photos, never the whole inspection (§27).
  maxPhotos: 12,
  maxNoteChars: AI_V2_LIMITS.maxNoteChars,
  maxJobIdChars: 80,
  maxStatusIds: 50,
  maxListJobs: 200,
  // 2026-10-19 (Owner: 「不要长篇大论」): 8 → 4 per list; the prompt asks for 3 / 3 / 2 / 3.
  maxListItems: 4,
  // AI observations of the row that travel with the review.
  maxObservations: 8,
  // Turns of the row's conversation that travel with a follow-up (the oldest are dropped first).
  maxTurns: 12,
  maxTurnChars: 2000,
  // Automatic provider calls inside ONE attempt: the initial call + 2 retries (§23).
  maxProviderCalls: 3,
  // Attempts a job may ever take (manual retries included) before it is final for good.
  maxAttempts: 6
});

export type AiJobTier = 'fast' | 'advanced';

export function isAiJobTier(value: unknown): value is AiJobTier {
  return value === 'fast' || value === 'advanced';
}

// Server-side job status (§24). The UI translates these; it never shows the identifier.
export type AiJobServerStatus = 'queued' | 'processing' | 'retrying' | 'completed' | 'inputInvalid' | 'providerUnavailable' | 'failedFinal' | 'interrupted';

export const AI_JOB_SERVER_STATUSES: readonly AiJobServerStatus[] = Object.freeze(['queued', 'processing', 'retrying', 'completed', 'inputInvalid', 'providerUnavailable', 'failedFinal', 'interrupted']);

export function isAiJobServerStatus(value: unknown): value is AiJobServerStatus {
  return typeof value === 'string' && (AI_JOB_SERVER_STATUSES as readonly string[]).includes(value);
}

// A job in one of these states is still being worked on by the server.
export const AI_JOB_ACTIVE_STATUSES: readonly AiJobServerStatus[] = Object.freeze(['queued', 'processing', 'retrying']);

export function isActiveJobStatus(status: AiJobServerStatus): boolean {
  return (AI_JOB_ACTIVE_STATUSES as readonly string[]).includes(status);
}

export type AiJobPushState = 'pending' | 'sent' | 'failed' | 'skipped';

export function isAiJobPushState(value: unknown): value is AiJobPushState {
  return value === 'pending' || value === 'sent' || value === 'failed' || value === 'skipped';
}

// What the completion notification names (§9): the area and the row — never a photo, never a client detail.
export interface AiJobSubject {
  areaName: string;
  itemTitle: string | null;
}

export type AdvancedReviewConfidence = 'low' | 'medium' | 'high';

// The structured second opinion (CA-9 / §11.6): the answer, what can be confirmed, what it might be,
// what cannot be confirmed from the photos, what to check on site, and the one photograph that would
// settle it. Nothing here is a decision and nothing here is report wording.
export interface AdvancedReviewResult {
  // The answer to the inspector's question and the overall assessment (2–4 sentences).
  summary: string;
  // 可确认: what the photos support.
  observations: string[];
  // 可能是什么: what it could mean.
  risks: string[];
  // 不能确认: what the photos cannot settle.
  unconfirmed: string[];
  // 建议现场: what to look at or do next, within a visual inspection.
  attention: string[];
  confidence: AdvancedReviewConfidence;
  // 需要补拍: ONE concrete photograph or piece of evidence that would settle it, or null.
  question: string | null;
}

export const ADVANCED_REVIEW_JSON_SCHEMA = Object.freeze({
  type: 'object',
  properties: {
    summary: { type: 'string' },
    observations: { type: 'array', items: { type: 'string' } },
    risks: { type: 'array', items: { type: 'string' } },
    unconfirmed: { type: 'array', items: { type: 'string' } },
    attention: { type: 'array', items: { type: 'string' } },
    confidence: { type: 'string', enum: ['low', 'medium', 'high'] },
    question: { type: ['string', 'null'] }
  },
  required: ['summary', 'observations', 'risks', 'unconfirmed', 'attention', 'confidence', 'question'],
  additionalProperties: false
});

export interface AiJobUsage {
  model: string;
  inputTokens: number;
  outputTokens: number;
  durationMs: number;
  providerCalls: number;
}

// One job as the server reports it.
export interface AiJobWire {
  jobId: string;
  tier: AiJobTier;
  status: AiJobServerStatus;
  attempt: number;
  maxAttempts: number;
  // Whether a manual retry may start another attempt from this state.
  retryable: boolean;
  errorCode: string | null;
  inspectionId: string;
  areaId: string;
  itemId: string;
  subject: AiJobSubject;
  result: AdvancedReviewResult | null;
  usage: AiJobUsage | null;
  pushState: AiJobPushState;
  createdAt: number;
  updatedAt: number;
  completedAt: number | null;
  viewedAt: number | null;
}

// The checklist row the review is about (§27; CA-9 §11.5): its identity, its guidance, its result
// and the inspector's note on it.
export interface AiJobItemContext {
  id: string;
  title: string;
  titleEn: string | null;
  hint: string;
  status: string;
  inspectorNote: string;
}

export type AiJobSubmitMode = 'new' | 'retry';

// One AI observation of the row, as the review reads it (report language; UI-language text when given).
export interface AiJobObservationContext {
  headline: string;
  observation: string;
  concern: string;
}

// One earlier turn of the row's conversation (CA-10 §12.2): the question asked and the answer given.
export interface AdvancedReviewTurn {
  question: string;
  answer: string;
}

// The conversation so far (a follow-up carries it; the first deep review carries none): the previous
// deep review and every question / answer after it, oldest first.
export interface AdvancedReviewPrior {
  summary: string;
  observations: string[];
  risks: string[];
  unconfirmed: string[];
  attention: string[];
  turns: AdvancedReviewTurn[];
}

export interface AdvancedReviewSubmitRequest {
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'submit';
  // 'new' never re-runs an existing job (double tap, refresh, retry storms); 'retry' may start a
  // controlled next attempt of a job that ended in a failure (§25).
  mode: AiJobSubmitMode;
  jobId: string;
  tier: 'advanced';
  inspectionId: string;
  areaId: string;
  itemId: string;
  uiLocale: AiV2Locale;
  reportLocale: AiV2Locale;
  area: AiV2AreaContext;
  item: AiJobItemContext;
  checklist: AiV2ChecklistItem[];
  scanNote: string;
  // A follow-up: the inspector's question. The first deep review: '' (the row's note is in `item`).
  question: string;
  // The row's AI observations (supporting context; CA-9 §11.5).
  aiObservations: AiJobObservationContext[];
  previousAdvanced: AdvancedReviewPrior | null;
  subject: AiJobSubject;
  photos: AiV2Photo[];
}

export interface AiJobsStatusRequest {
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'status';
  // Empty = every recent job of the account.
  jobIds: string[];
  inspectionId: string | null;
}

export type AiJobsDevicePlatform = 'ios' | 'android' | 'web';
export type AiJobsPushEnvironment = 'sandbox' | 'production' | 'unknown';

export interface AiJobsRegisterDeviceRequest {
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'registerDevice';
  token: string;
  platform: AiJobsDevicePlatform;
  environment: AiJobsPushEnvironment;
  appVersion: string;
}

// Account isolation (2026-09-21): the account that signs out of a phone stops owning that phone's
// token, so a completion push of ITS jobs never lands on the phone the next inspector is using. The
// account is the authenticated caller — never a field of the request — so a caller can only ever
// remove a registration of its own. Additive under protocol version 1: a function deployed before
// this action answers `badRequest` (unknown action), which the app treats as "not removed".
export interface AiJobsUnregisterDeviceRequest {
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'unregisterDevice';
  token: string;
}

export interface AiJobsMarkViewedRequest {
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'markViewed';
  jobId: string;
}

// Owner decision list 2026-10-19 items 1 / 2: generating the final report deletes the inspection's process
// data on the server too — every AI review record of that inspection. The caller can only purge its own.
export interface AiJobsPurgeInspectionRequest {
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'purgeInspection';
  inspectionId: string;
}

export type AiJobsRequest = AdvancedReviewSubmitRequest | AiJobsStatusRequest | AiJobsRegisterDeviceRequest | AiJobsUnregisterDeviceRequest | AiJobsMarkViewedRequest | AiJobsPurgeInspectionRequest;

export type AiJobsErrorCode = AiV2ErrorCode | 'notFound' | 'internalError';

export const AI_JOBS_ERROR_STATUS: Readonly<Record<AiJobsErrorCode, number>> = Object.freeze({ ...AI_V2_ERROR_STATUS, notFound: 404, internalError: 500 });

export interface AiJobsErrorResponse {
  ok: false;
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  code: AiJobsErrorCode;
  message: string;
  retryable: boolean;
  quota?: AiV2Quota;
}

export interface AiJobsSubmitResponse {
  ok: true;
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'submit';
  job: AiJobWire;
  // true = this call did not create the job (it already existed).
  existing: boolean;
  // true = this call started an attempt (the worker is running in the background).
  started: boolean;
  quota?: AiV2Quota;
}

export interface AiJobsStatusResponse {
  ok: true;
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'status';
  jobs: AiJobWire[];
}

export interface AiJobsRegisterDeviceResponse {
  ok: true;
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'registerDevice';
  registered: boolean;
}

export interface AiJobsUnregisterDeviceResponse {
  ok: true;
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'unregisterDevice';
  // true = the account held this token and no longer does; false = there was nothing to remove.
  // Either way the account does not own the token any more (the action is idempotent).
  removed: boolean;
}

export interface AiJobsMarkViewedResponse {
  ok: true;
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'markViewed';
  job: AiJobWire | null;
}

export interface AiJobsPurgeInspectionResponse {
  ok: true;
  protocolVersion: typeof AI_JOBS_PROTOCOL_VERSION;
  action: 'purgeInspection';
  // How many AI review records were deleted (0 when there were none — the action is idempotent).
  deleted: number;
}

export type AiJobsOkResponse = AiJobsSubmitResponse | AiJobsStatusResponse | AiJobsRegisterDeviceResponse | AiJobsUnregisterDeviceResponse | AiJobsMarkViewedResponse | AiJobsPurgeInspectionResponse;
export type AiJobsResponse = AiJobsOkResponse | AiJobsErrorResponse;

export function aiJobsError(code: AiJobsErrorCode, message: string, retryable?: boolean): AiJobsErrorResponse {
  const defaultRetryable = code === 'timeout' || code === 'rateLimited' || code === 'providerError' || code === 'malformedResponse' || code === 'unavailable' || code === 'billingUnavailable' || code === 'internalError';
  return { ok: false, protocolVersion: AI_JOBS_PROTOCOL_VERSION, code, message, retryable: retryable ?? defaultRetryable };
}

// ---------- validation (shared) ----------

type Rec = Record<string, unknown>;

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v : null;
}

function strArray(v: unknown, maxItems: number): string[] {
  return Array.isArray(v)
    ? v
        .filter((x): x is string => typeof x === 'string')
        .map((x) => x.trim())
        .filter((x) => x.length > 0)
        .slice(0, maxItems)
    : [];
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function idOk(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0 && v.length <= AI_JOBS_LIMITS.maxJobIdChars;
}

// A push device token as it is stored: trimmed. Registering and unregistering read it through this
// ONE function, so the token a device removes is always the token it registered.
function deviceTokenOf(v: unknown): string | null {
  if (typeof v !== 'string' || v.trim().length === 0 || v.length > 400) return null;
  return v.trim();
}

function checkTurns(raw: unknown): AdvancedReviewTurn[] {
  if (!Array.isArray(raw)) return [];
  const turns: AdvancedReviewTurn[] = [];
  for (const t of raw) {
    if (!isRec(t)) continue;
    const question = str(t.question).trim().slice(0, AI_JOBS_LIMITS.maxTurnChars);
    const answer = str(t.answer).trim().slice(0, AI_JOBS_LIMITS.maxTurnChars);
    if (question.length === 0 && answer.length === 0) continue;
    turns.push({ question, answer });
  }
  return turns.slice(-AI_JOBS_LIMITS.maxTurns);
}

function checkAdvancedPrior(raw: unknown): AdvancedReviewPrior | null {
  if (!isRec(raw)) return null;
  const summary = str(raw.summary).trim().slice(0, 2000);
  const observations = strArray(raw.observations, AI_JOBS_LIMITS.maxListItems);
  const risks = strArray(raw.risks, AI_JOBS_LIMITS.maxListItems);
  const unconfirmed = strArray(raw.unconfirmed, AI_JOBS_LIMITS.maxListItems);
  const attention = strArray(raw.attention, AI_JOBS_LIMITS.maxListItems);
  if (summary.length === 0 && observations.length === 0) return null;
  return { summary, observations, risks, unconfirmed, attention, turns: checkTurns(raw.turns) };
}

function checkObservations(raw: unknown): AiJobObservationContext[] {
  if (!Array.isArray(raw)) return [];
  const out: AiJobObservationContext[] = [];
  for (const o of raw) {
    if (!isRec(o)) continue;
    const headline = str(o.headline).trim().slice(0, 400);
    const observation = str(o.observation).trim().slice(0, 1200);
    const concern = str(o.concern).trim().slice(0, 600);
    if (headline.length === 0 && observation.length === 0) continue;
    out.push({ headline, observation, concern });
  }
  return out.slice(0, AI_JOBS_LIMITS.maxObservations);
}

// A summary that stops before the end of a sentence was cut short (E2E walkthrough 2026-10-16: the
// summary was 「就您笔记中」 while every list was full — the model opened an ASCII quote inside a
// Chinese sentence and, under structured output, that quote ended the JSON string). A summary always
// ends a sentence; closing quotes and brackets after the stop are fine.
export function advancedSummaryCutShort(summary: string): boolean {
  const s = summary.trim();
  return s.length === 0 || !/[.!?。！？…]["'”’』」）)]*$/.test(s);
}

// A syntactically valid answer that is not a whole answer: the summary was cut short (whatever the
// lists say), or nothing was said at all (Owner device test 2026-09-13: summary "从照片无法确定是否",
// every list empty). The service asks once more before it persists such an answer.
export function isDegenerateAdvancedResult(result: AdvancedReviewResult): boolean {
  if (advancedSummaryCutShort(result.summary)) return true;
  const listsEmpty = result.observations.length === 0 && result.risks.length === 0 && result.unconfirmed.length === 0 && result.attention.length === 0;
  return listsEmpty && result.summary.trim().length < 12;
}

// The provider's JSON → a validated result. Never throws; null when the shape is unusable.
export function normalizeAdvancedReviewResult(raw: unknown): AdvancedReviewResult | null {
  if (!isRec(raw)) return null;
  const summary = str(raw.summary).trim();
  const observations = strArray(raw.observations, AI_JOBS_LIMITS.maxListItems);
  if (summary.length === 0 && observations.length === 0) return null;
  const confidence: AdvancedReviewConfidence = raw.confidence === 'low' || raw.confidence === 'medium' || raw.confidence === 'high' ? raw.confidence : 'low';
  return {
    summary,
    observations,
    risks: strArray(raw.risks, AI_JOBS_LIMITS.maxListItems),
    unconfirmed: strArray(raw.unconfirmed, AI_JOBS_LIMITS.maxListItems),
    attention: strArray(raw.attention, AI_JOBS_LIMITS.maxListItems),
    confidence,
    question: strOrNull(raw.question)
  };
}

export function checkAiJobUsage(raw: unknown): AiJobUsage | null {
  if (!isRec(raw)) return null;
  return { model: str(raw.model), inputTokens: num(raw.inputTokens, 0), outputTokens: num(raw.outputTokens, 0), durationMs: num(raw.durationMs, 0), providerCalls: num(raw.providerCalls, 0) };
}

export function checkAiJobSubject(raw: unknown): AiJobSubject {
  if (!isRec(raw)) return { areaName: '', itemTitle: null };
  return { areaName: str(raw.areaName), itemTitle: strOrNull(raw.itemTitle) };
}

// A job as reported on the wire, validated field by field (the client relies on every one of these).
export function checkAiJobWire(raw: unknown): AiJobWire | null {
  if (!isRec(raw) || !idOk(raw.jobId) || !isAiJobTier(raw.tier) || !isAiJobServerStatus(raw.status)) return null;
  if (!idOk(raw.inspectionId) || !idOk(raw.areaId) || !idOk(raw.itemId)) return null;
  return {
    jobId: raw.jobId,
    tier: raw.tier,
    status: raw.status,
    attempt: Math.max(1, Math.floor(num(raw.attempt, 1))),
    maxAttempts: Math.max(1, Math.floor(num(raw.maxAttempts, AI_JOBS_LIMITS.maxAttempts))),
    retryable: raw.retryable === true,
    errorCode: strOrNull(raw.errorCode),
    inspectionId: raw.inspectionId,
    areaId: raw.areaId,
    itemId: raw.itemId,
    subject: checkAiJobSubject(raw.subject),
    result: raw.result === null || raw.result === undefined ? null : normalizeAdvancedReviewResult(raw.result),
    usage: raw.usage === null || raw.usage === undefined ? null : checkAiJobUsage(raw.usage),
    pushState: isAiJobPushState(raw.pushState) ? raw.pushState : 'pending',
    createdAt: num(raw.createdAt, 0),
    updatedAt: num(raw.updatedAt, 0),
    completedAt: numOrNull(raw.completedAt),
    viewedAt: numOrNull(raw.viewedAt)
  };
}

export type AiJobsRequestCheck = { ok: true; request: AiJobsRequest } | { ok: false; code: AiJobsErrorCode; message: string };

function checkPhotos(raw: unknown): { ok: true; photos: AiV2Photo[] } | { ok: false; code: AiJobsErrorCode; message: string } {
  if (!Array.isArray(raw) || raw.length === 0) return { ok: false, code: 'badRequest', message: 'at least one photo is required' };
  if (raw.length > AI_JOBS_LIMITS.maxPhotos) return { ok: false, code: 'tooManyImages', message: `at most ${AI_JOBS_LIMITS.maxPhotos} photos per advanced review` };
  const photos: AiV2Photo[] = [];
  const seen = new Set<string>();
  for (const p of raw) {
    if (!isRec(p) || typeof p.id !== 'string' || p.id.length === 0 || seen.has(p.id)) return { ok: false, code: 'badRequest', message: 'photo id missing or duplicated' };
    if (!isAiV2MediaType(p.mediaType)) return { ok: false, code: 'unsupportedImage', message: `unsupported image type ${String(p.mediaType)}` };
    if (typeof p.base64 !== 'string' || p.base64.length === 0) return { ok: false, code: 'unsupportedImage', message: 'photo bytes missing' };
    if (p.base64.length > AI_V2_LIMITS.maxPhotoBase64Chars) return { ok: false, code: 'tooLarge', message: 'one photo exceeds the size limit' };
    const note = str(p.note);
    if (note.length > AI_JOBS_LIMITS.maxNoteChars) return { ok: false, code: 'badRequest', message: 'photo note too long' };
    seen.add(p.id);
    photos.push({ id: p.id, mediaType: p.mediaType, base64: p.base64, note, capturedAt: typeof p.capturedAt === 'number' ? p.capturedAt : null });
  }
  return { ok: true, photos };
}

// Validates an incoming request body (the function) or an outgoing one (the client, before upload).
export function checkAiJobsRequest(raw: unknown): AiJobsRequestCheck {
  if (!isRec(raw)) return { ok: false, code: 'badRequest', message: 'request body is not an object' };
  if (raw.protocolVersion !== AI_JOBS_PROTOCOL_VERSION) return { ok: false, code: 'badRequest', message: `unsupported protocolVersion ${String(raw.protocolVersion)}` };
  switch (raw.action) {
    case 'status': {
      const jobIds = Array.isArray(raw.jobIds) ? raw.jobIds.filter(idOk).slice(0, AI_JOBS_LIMITS.maxStatusIds) : [];
      return { ok: true, request: { protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'status', jobIds, inspectionId: idOk(raw.inspectionId) ? raw.inspectionId : null } };
    }
    case 'registerDevice': {
      const token = deviceTokenOf(raw.token);
      if (token === null) return { ok: false, code: 'badRequest', message: 'device token missing' };
      const platform: AiJobsDevicePlatform = raw.platform === 'android' ? 'android' : raw.platform === 'web' ? 'web' : 'ios';
      const environment: AiJobsPushEnvironment = raw.environment === 'sandbox' || raw.environment === 'production' ? raw.environment : 'unknown';
      return { ok: true, request: { protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'registerDevice', token, platform, environment, appVersion: str(raw.appVersion).slice(0, 40) } };
    }
    case 'unregisterDevice': {
      // The token and nothing else: WHOSE registration is removed is the authenticated caller's.
      const token = deviceTokenOf(raw.token);
      if (token === null) return { ok: false, code: 'badRequest', message: 'device token missing' };
      return { ok: true, request: { protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'unregisterDevice', token } };
    }
    case 'markViewed': {
      if (!idOk(raw.jobId)) return { ok: false, code: 'badRequest', message: 'jobId missing' };
      return { ok: true, request: { protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'markViewed', jobId: raw.jobId } };
    }
    case 'purgeInspection': {
      if (!idOk(raw.inspectionId)) return { ok: false, code: 'badRequest', message: 'inspectionId missing' };
      return { ok: true, request: { protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'purgeInspection', inspectionId: raw.inspectionId } };
    }
    case 'submit': {
      if (raw.tier !== 'advanced') return { ok: false, code: 'badRequest', message: 'only the advanced tier is submitted as a job' };
      if (!idOk(raw.jobId)) return { ok: false, code: 'badRequest', message: 'jobId missing' };
      if (!idOk(raw.inspectionId) || !idOk(raw.areaId) || !idOk(raw.itemId)) return { ok: false, code: 'badRequest', message: 'inspectionId / areaId / itemId missing' };
      if (!isAiV2Locale(raw.uiLocale) || !isAiV2Locale(raw.reportLocale)) return { ok: false, code: 'badRequest', message: 'uiLocale / reportLocale must be zh-CN or en-US' };
      if (!isRec(raw.area) || typeof raw.area.areaId !== 'string' || typeof raw.area.areaName !== 'string') return { ok: false, code: 'badRequest', message: 'area context missing' };
      if (!Array.isArray(raw.checklist) || raw.checklist.length > AI_V2_LIMITS.maxChecklistItems) return { ok: false, code: 'badRequest', message: 'checklist missing or too long' };
      const photos = checkPhotos(raw.photos);
      if (!photos.ok) return photos;
      const scanNote = str(raw.scanNote);
      const question = str(raw.question);
      if (scanNote.length > AI_JOBS_LIMITS.maxNoteChars || question.length > AI_JOBS_LIMITS.maxNoteChars) return { ok: false, code: 'badRequest', message: 'note too long' };
      const checklist: AiV2ChecklistItem[] = [];
      for (const c of raw.checklist) {
        if (!isRec(c) || typeof c.id !== 'string' || typeof c.title !== 'string') return { ok: false, code: 'badRequest', message: 'checklist item malformed' };
        checklist.push({ id: c.id, title: c.title, titleEn: strOrNull(c.titleEn), hint: str(c.hint), status: str(c.status, 'notChecked') });
      }
      if (!isRec(raw.item) || typeof raw.item.id !== 'string' || typeof raw.item.title !== 'string' || raw.item.id !== raw.itemId) return { ok: false, code: 'badRequest', message: 'item context missing' };
      const note = str(raw.item.inspectorNote);
      if (note.length > AI_JOBS_LIMITS.maxNoteChars) return { ok: false, code: 'badRequest', message: 'item note too long' };
      const item: AiJobItemContext = { id: raw.item.id, title: raw.item.title, titleEn: strOrNull(raw.item.titleEn), hint: str(raw.item.hint), status: str(raw.item.status, 'notChecked'), inspectorNote: note };
      const area: AiV2AreaContext = {
        areaId: raw.area.areaId,
        areaName: raw.area.areaName,
        areaNameEn: strOrNull(raw.area.areaNameEn),
        areaKind: str(raw.area.areaKind, 'other'),
        floor: typeof raw.area.floor === 'number' ? raw.area.floor : null,
        propertyType: strOrNull(raw.area.propertyType),
        yearBuilt: typeof raw.area.yearBuilt === 'number' ? raw.area.yearBuilt : null
      };
      return {
        ok: true,
        request: {
          protocolVersion: AI_JOBS_PROTOCOL_VERSION,
          action: 'submit',
          mode: raw.mode === 'retry' ? 'retry' : 'new',
          jobId: raw.jobId,
          tier: 'advanced',
          inspectionId: raw.inspectionId,
          areaId: raw.areaId,
          itemId: raw.itemId,
          uiLocale: raw.uiLocale,
          reportLocale: raw.reportLocale,
          area,
          item,
          checklist,
          scanNote,
          question,
          aiObservations: checkObservations(raw.aiObservations),
          previousAdvanced: checkAdvancedPrior(raw.previousAdvanced),
          subject: checkAiJobSubject(raw.subject),
          photos: photos.photos
        }
      };
    }
    default:
      return { ok: false, code: 'badRequest', message: 'unknown action' };
  }
}

// Client-side check of an answer: everything the UI relies on must be present and typed.
export function checkAiJobsResponse(raw: unknown): AiJobsResponse | null {
  if (!isRec(raw) || raw.protocolVersion !== AI_JOBS_PROTOCOL_VERSION) return null;
  const quota = 'quota' in raw ? checkAiV2Quota(raw.quota) : null;
  if (raw.ok === false) {
    const code = str(raw.code) as AiJobsErrorCode;
    if (!(code in AI_JOBS_ERROR_STATUS)) return null;
    const error: AiJobsErrorResponse = { ok: false, protocolVersion: AI_JOBS_PROTOCOL_VERSION, code, message: str(raw.message), retryable: raw.retryable === true };
    if (quota) error.quota = quota;
    return error;
  }
  if (raw.ok !== true) return null;
  switch (raw.action) {
    case 'submit': {
      const job = checkAiJobWire(raw.job);
      if (!job) return null;
      const response: AiJobsSubmitResponse = { ok: true, protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'submit', job, existing: raw.existing === true, started: raw.started === true };
      if (quota) response.quota = quota;
      return response;
    }
    case 'status': {
      if (!Array.isArray(raw.jobs)) return null;
      const jobs: AiJobWire[] = [];
      for (const j of raw.jobs) {
        const job = checkAiJobWire(j);
        if (job) jobs.push(job);
      }
      return { ok: true, protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'status', jobs };
    }
    case 'registerDevice':
      return { ok: true, protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'registerDevice', registered: raw.registered === true };
    case 'unregisterDevice':
      return { ok: true, protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'unregisterDevice', removed: raw.removed === true };
    case 'markViewed':
      return { ok: true, protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'markViewed', job: raw.job === null || raw.job === undefined ? null : checkAiJobWire(raw.job) };
    case 'purgeInspection':
      return { ok: true, protocolVersion: AI_JOBS_PROTOCOL_VERSION, action: 'purgeInspection', deleted: typeof raw.deleted === 'number' && Number.isFinite(raw.deleted) ? Math.max(0, Math.floor(raw.deleted)) : 0 };
    default:
      return null;
  }
}

// The Fast AI protocol version the advanced request's area / checklist context follows.
export const AI_JOBS_CONTEXT_PROTOCOL_VERSION = AI_V2_PROTOCOL_VERSION;
